import type { SkillFile } from '../lib/api';

interface FileTabsProps {
  active: string;              // 'SKILL.md' or a rel_path like 'references/api-errors.md'
  files: SkillFile[];
  dirtyPaths: Set<string>;     // paths with unsaved changes
  onSelect: (path: string) => void;
  onAddFile: () => void;
}

/**
 * Editor file tabs row. SKILL.md is always first; reference/script/eval files
 * follow in rel_path order. Dirty-dot rendered on any tab with pending changes,
 * matching the C3 "blast-radius dirty-dot propagation" requirement.
 */
export function FileTabs({ active, files, dirtyPaths, onSelect, onAddFile }: FileTabsProps) {
  return (
    <div className="hairline-b flex items-center gap-1 bg-surface px-2 pt-1 overflow-x-auto">
      <FileTab
        path="SKILL.md"
        label="SKILL.md"
        active={active === 'SKILL.md'}
        dirty={dirtyPaths.has('SKILL.md')}
        onClick={() => onSelect('SKILL.md')}
        kind="primary"
      />
      {files.map((f) => (
        <FileTab
          key={f.rel_path}
          path={f.rel_path}
          label={shortLabel(f.rel_path)}
          active={active === f.rel_path}
          dirty={dirtyPaths.has(f.rel_path)}
          onClick={() => onSelect(f.rel_path)}
          kind={kindFor(f.rel_path)}
        />
      ))}
      <button
        type="button"
        onClick={onAddFile}
        title="Add a reference / script / eval file"
        className="ml-1 mt-1 rounded-t border-x border-t border-b-transparent px-2 py-1 text-[11px] text-text-subtle hover:text-[var(--color-fg)] hover:bg-canvas"
      >
        + add file
      </button>
    </div>
  );
}

function FileTab({
  path: _path,
  label,
  active,
  dirty,
  onClick,
  kind,
}: {
  path: string;
  label: string;
  active: boolean;
  dirty: boolean;
  onClick: () => void;
  kind: 'primary' | 'reference' | 'script' | 'eval';
}) {
  const base = 'group relative mt-1 flex items-center gap-1.5 rounded-t border-x border-t px-2 py-1 text-[11px] cursor-pointer';
  const activeStyles = active
    ? 'bg-canvas border-hairline-strong text-text-fg'
    : 'bg-surface-2 border-hairline text-text-muted hover:text-[var(--color-fg)]';
  const kindIcon =
    kind === 'primary' ? '●' : kind === 'reference' ? '◇' : kind === 'script' ? '⌘' : '✓';
  return (
    <button type="button" onClick={onClick} className={`${base} ${activeStyles}`}>
      <span className="text-text-subtle">{kindIcon}</span>
      <span className="font-mono">{label}</span>
      {dirty && (
        <span
          aria-label="unsaved changes"
          className="ml-0.5 h-[6px] w-[6px] rounded-full"
          style={{ background: 'var(--color-warning)' }}
        />
      )}
    </button>
  );
}

function shortLabel(relPath: string): string {
  const parts = relPath.split('/');
  return parts.length > 1 ? parts[parts.length - 1] : relPath;
}

function kindFor(relPath: string): 'reference' | 'script' | 'eval' {
  if (relPath.startsWith('scripts/')) return 'script';
  if (relPath.startsWith('evals/')) return 'eval';
  return 'reference';
}
